import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link, useLocation, Outlet } from "react-router-dom";
import "../style/MainLayout.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import { ROUTES } from "../Routes/routes";
import { toast } from "react-toastify";
import { getUserIdFromToken, getDecodedUser } from "../utils/auth";
import NotificationBell from "./NotificationBell";
import Swal from "sweetalert2";
import { PredefinedWord } from "../Routes/Predefined";
import { apiAxios } from "../utils/apiAxios";
import AlertSoundManager from "../utils/AlertSoundManager";

type MenuGroup = {
    title: string;
    icon: string;
    items: any[];
};
type MenuItem = { key: string; label: string; icon: string; iconImage: string; disabled?: boolean; }

type GenericLayoutProps = {
    children?: React.ReactNode;
    pageTitle?: string;
    menu: MenuItem[];
    // masterMenu?: any[];
    // settingsMenu?: any[];
    menuGroups?: MenuGroup[];
    titleLabel: string;
    profileRoute: string;
    getPageTitle?: (path: string) => string;
    showNotifications?: boolean;
    apiNotificationUrl?: string; // Keep this prop for backward compatibility
};

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const GenericLayout: React.FC<GenericLayoutProps> = ({
    children,
    pageTitle,
    menu,
    // masterMenu,
    //settingsMenu,
    menuGroups,
    titleLabel,
    profileRoute,
    getPageTitle,
    showNotifications = true,
    apiNotificationUrl: propNotificationUrl, // Rename to avoid confusion
}) => {
    const [openGroups, setOpenGroups] = useState<string | null>(null);
    const [collapsed, setCollapsed] = useState(false);
    const [openMaster, setOpenMaster] = useState(false);
    const [userName, setUserName] = useState<string | null>(null);
    const [userProfileImage, setUserProfileImage] = useState<string | null>(null);
    const [userId, setUserId] = useState<number | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [clinics, setClinics] = useState<any[]>([]);
    const [selectedClinic, setSelectedClinic] = useState<any>(null);
    const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [isOnline, setIsOnline] = useState(false);
    const [RoleName, setRoleName] = useState<any>(null);
    const masterRef = useRef<HTMLDivElement | null>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [openSettings, setOpenSettings] = useState(false);
    const settingsRef = useRef<HTMLDivElement | null>(null);
    const subtitle = userRole;
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [imgError, setImgError] = useState(false);

    const location = useLocation();

    const isDashboardPage =
        location.pathname === ROUTES.DENTIST.DENTIST_DASHBOARD ||
        location.pathname === ROUTES.CENTRAL.CENTRAL_HUB_DASHBOARD ||
        location.pathname === ROUTES.MILLING.MILLING_HUB_DASHBOARD;

    const user = useMemo(() => {
        const storedUser = sessionStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    }, []);

    // ✅ Get notification URL based on user role
    const getNotificationUrl = (): string => {
        // If prop is provided, use it (for backward compatibility)
        if (propNotificationUrl) {
            return propNotificationUrl;
        }

        // Otherwise determine based on user role
        switch (userRole) {
            case 'Designer':
                return `${API_BASE}/Notification/designer-notifications`;
            case 'Milling Hub':
                return `${API_BASE}/Notification/milling-notifications`;
            case 'Central Hub':
                return `${API_BASE}/Notification/central-notifications`;
            case 'Manager':
                return `${API_BASE}/Notification/manager-notifications`;
            case 'Admin':
                return `${API_BASE}/Notification/admin-notifications`;
            default:
                return `${API_BASE}/Notification/notifications`;
        }
    };

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;

            // ✅ if click is OUTSIDE dropdown → close
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const toggleGroup = (title: string) => {
        setOpenGroups((prev) => (prev === title ? null : title));
    };
    const mergedMenu = useMemo(() => {
        const normalizedMenu =
            menu?.map((m) => ({
                ...m,
                type: "item",
            })) || [];

        const normalizedGroups =
            menuGroups?.map((g) => ({
                ...g,
                type: "group",
            })) || [];

        return [...normalizedMenu, ...normalizedGroups]
            .sort((a: any, b: any) => (a.order ?? 999) - (b.order ?? 999));
    }, [menu, menuGroups]);

    useEffect(() => {
        // ✅ Load initially from sessionStorage
        const storedImage = sessionStorage.getItem("profileImage");
        if (storedImage) {
            setUserProfileImage(storedImage);
        }

        // ✅ Listen for updates
        const handleProfileUpdate = () => {
            const updatedImage = sessionStorage.getItem("profileImage");
            setUserProfileImage(updatedImage);
        };

        window.addEventListener("profileImageUpdated", handleProfileUpdate);

        return () => {
            window.removeEventListener("profileImageUpdated", handleProfileUpdate);
        };
    }, []);

    // ✅ Responsive handling
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const mobile = width <= 768;
            const tablet = width > 768 && width <= 1024;

            setIsMobile(mobile);
            setIsTablet(tablet);

            if (mobile || tablet) {
                setCollapsed(true);
            } else {
                setCollapsed(false);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const user = getDecodedUser<any>();
        const id = getUserIdFromToken();

        if (user) {
            setUserName(
                user.name ||
                user.unique_name ||
                user.email ||
                "User"
            );
            setUserRole(user.role || user.UserRole || null);
            setUserId(id);
        }
    }, []);
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (!userId) return;

            try {
                const token = sessionStorage.getItem("token");

                const res = await apiAxios.get(
                    `${API_BASE}/Users/GetUser/${userId}`,

                );

                if (res.data?.imageUrl) {
                    setUserProfileImage(`${res.data.imageUrl}`);
                    sessionStorage.setItem("profileImage", res.data.imageUrl);
                }
            } catch (error) {
                console.error("Failed to load profile image", error);
            }
        };

        fetchUserProfile();
    }, [userId]);

    useEffect(() => {
        const handleSidebarClick = (event: MouseEvent) => {
            const target = event.target as Node;

            if (!openMaster) return;

            if (masterRef.current?.contains(target)) {
                return;
            }

            const sidebar = sidebarRef.current;
            if (sidebar) {
                const rect = sidebar.getBoundingClientRect();
                const clickX = event.clientX;

                const isScrollbarClick = clickX > rect.right - 20;

                if (isScrollbarClick) {
                    return;
                }
            }

            setOpenMaster(false);
        };

        document.addEventListener("mousedown", handleSidebarClick);

        return () => {
            document.removeEventListener("mousedown", handleSidebarClick);
        };
    }, [openMaster]);

    const handleMenuClick = () => {
        if (isMobile || isTablet) {
            setCollapsed(true);
            setOpenMaster(false);
        }
    };
    const fetchClinics = async () => {
        try {
            const token = sessionStorage.getItem("token");
            if (!token) return;

            const response = await apiAxios.get(
                `${API_BASE}/ClinicDetails/GetAllClinicsforDD`,

            );

            const clinicList = response.data?.data || response.data || [];
            setClinics(clinicList);
            const savedClinic = sessionStorage.getItem("selectedClinic");

            if (savedClinic) {
                setSelectedClinic(JSON.parse(savedClinic));
            } else if (clinicList.length > 0) {
                setSelectedClinic(clinicList[0]);
                sessionStorage.setItem(
                    "selectedClinic",
                    JSON.stringify(clinicList[0])
                );
            }
        } catch (error) {
            console.error("Failed to fetch clinics", error);
        }
    };

    useEffect(() => {
        fetchClinics();
    }, []);

    useEffect(() => {
        const loadClinic = () => {
            const clinicId = sessionStorage.getItem("selectedClinicId");
            setSelectedClinicId(clinicId ? Number(clinicId) : null);
        };

        loadClinic();
        window.addEventListener("clinicChanged", loadClinic);

        return () => {
            window.removeEventListener("clinicChanged", loadClinic);
        };
    }, []);

    // ✅ Click outside handlers
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            // If click is inside settings → do nothing
            if (settingsRef.current?.contains(target)) {
                return;
            }

            // If click is inside sidebar but NOT inside settings → close
            if (sidebarRef.current?.contains(target)) {
                setOpenSettings(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleClinicSelect = (clinic: any) => {
        const orderStarted = sessionStorage.getItem("orderStarted");

        if (orderStarted === "true") {
            toast.warning("Clinic cannot be changed after selecting teeth.");
            return;
        }
        setSelectedClinic(clinic);
        sessionStorage.setItem("selectedClinic", JSON.stringify(clinic));
        sessionStorage.setItem("selectedClinicId", clinic.clinicID.toString());
        window.dispatchEvent(new Event("clinicChanged"));
        toast.success(`Clinic switched to ${clinic.clinicName}`);
    };

    // ✅ Fetch user status
    useEffect(() => {
        const fetchStatus = async () => {
            if (!userId) return;

            try {
                const res = await apiAxios.get(
                    `${API_BASE}/Users/get-status/${userId}`
                );
                setIsOnline(res.data.isOnline === true);
            } catch (err) {
                console.warn("Status fetch failed");
            }
        };
        fetchStatus();
    }, [userId]);

    const updateOnlineStatus = async (status: boolean, force: boolean = false) => {
        try {
            const token = sessionStorage.getItem("token");
            if (!token || !userId) return;

            await apiAxios.post(
                `${API_BASE}/Users/update-status`,
                {
                    userId: userId,
                    isOnline: status,
                    force: force
                },

            );

            setIsOnline(status);

            // ✅ FIX: make sure any previous SweetAlert popup (the
            // "Go Online? / Go Offline?" confirmation) is fully closed
            // before opening the success popup. Without this, firing a
            // new Swal.fire immediately after the previous one resolves
            // can leave the old backdrop/overlay stuck on screen so it
            // looks like "popup close nahi ho raha".
            Swal.close();

            Swal.fire({
                icon: "success",
                title: status ? "You are Online" : "You are Offline",
                timer: 1500,
                showConfirmButton: false
            });

        } catch (error: any) {

            const message = error.response?.data?.message;

            if (message?.includes("working hours")) {

                // ✅ FIX: close the previous popup before opening this one too
                Swal.close();

                const forceResult = await Swal.fire({
                    title: "Outside Working Hours",
                    text: message,
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Force Online",
                    cancelButtonText: "Cancel",
                    confirmButtonColor: "#0d6efd",
                    customClass: {
                        popup: "my-popupsla",
                        title: "my-titlesla",
                        htmlContainer: "my-textsla",

                    },
                });

                if (forceResult.isConfirmed) {
                    updateOnlineStatus(status, true);
                }

            } else {

                Swal.close();

                Swal.fire({
                    icon: "error",
                    title: "Status Update Failed",
                    text: message || "Something went wrong"
                });

            }
        }
    };

    const handleLogout = async () => {
        try {
            AlertSoundManager.stop(); // <-- stop immediately

            const token = sessionStorage.getItem("token");
            if (token)
                await apiAxios.post(
                    `${API_BASE}/Auth/logout`,
                    {},

                );
        } catch {
            console.warn("Logout failed or expired");
        } finally {
            sessionStorage.clear();
            navigate(ROUTES.HOME);
            window.dispatchEvent(new Event("storage"));
        }
    };

    const handleProfileClick = () => {
        if (!userRole) return;

        if (userRole === "Dentist") {
            setRoleName("Dentist");
            navigate(ROUTES.DENTIST.DENTIST_PROFILE);
        }
        else if (userRole === "Central Hub") {
            setRoleName("Central Hub");
            navigate(ROUTES.CENTRAL.CENTRAL_PROFILE);
        }
        else if (userRole === "Milling Hub") {
            setRoleName("Milling Hub");
            navigate(ROUTES.MILLING.MILLING_PROFILE);
        }
        else if (userRole === "Manager") {
            setRoleName("Manager");
            navigate(ROUTES.CENTRAL.Manager.MANAGER_ManagerProfile);
        }
        else if (userRole === "Designer") {
            setRoleName("Designer");
            navigate(ROUTES.CENTRAL.Designer.DESIGNER_PROFILE);
        }
        else if (userRole === "Delivery") {
            setRoleName("Delivery");
            navigate(ROUTES.DELIVERYPARTNER.DELIVERYPARTNER_DeliverypartnerProfile);
        }
        else {
            toast.error("Invalid user role");
        }
    };

    const handleChangePasswordClick = () => {
        if (!userRole) return;

        const routeMap: Record<string, string> = {
            "Dentist": ROUTES.DENTIST.ChangePassword,
            "Central Hub": ROUTES.CENTRAL.ChangePassword,
            "Milling Hub": ROUTES.MILLING.ChangePassword,
            "Manager": ROUTES.CENTRAL.Manager.ChangePassword,
            "Designer": ROUTES.CENTRAL.Designer.ChangePassword,
            "Delivery": ROUTES.DELIVERYPARTNER.DELIVERYPARTNER_ChangePassword
        };

        const route = routeMap[userRole];

        if (route) {
            navigate(route);
        } else {
            toast.error("Change Password route not defined for this role");
        }
    };
    const getUserInitials = (fullName?: string) => {
        if (!fullName) return "";
        const words = fullName.trim().split(" ");
        if (words.length === 1) {
            return words[0][0].toUpperCase();
        }
        return (
            words[0][0].toUpperCase() +
            words[words.length - 1][0].toUpperCase()
        );
    };

    const currentTitle = pageTitle || getPageTitle?.(location.pathname) || "Dashboard";

    const [clinicOpen, setClinicOpen] = useState(false);

    const navigate = useNavigate();
    return (
        <div className={`main-layout ${collapsed ? "collapsed" : ""}`}>
            {/* Sidebar */}
            <aside ref={sidebarRef} className={`sidebar ${isMobile ? "mobile-sidebar" : ""}`}>
                <div className="sidebar-header d-flex align-items-center px-2">
                    <div
                        className={`align-items-center lab-console-icon ${collapsed ? "justify-content-center w-100" : "gap-2"}`}
                    >
                        <div className="profile-imagenamediv">
                            {!collapsed && (
                                <div className="namewelcome-div">
                                    <div className="welcome-heading"><img src="/LogoContainer.png" alt="Logo" /></div>
                                </div>
                            )}
                            {collapsed && (
                                <div className="image-div-small">
                                    <div className="welcome-heading"><img src="/smalltoothicon.png" className="small-logo-image" alt="Logo" /></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <button className="btn btn-sm btn-toggle" onClick={() => setCollapsed(!collapsed)} >
                        <i className={`bi ${collapsed ? "bi bi-chevron-right" : "bi bi-chevron-left"} toggle-iconsidebar`} />
                    </button>
                </div>

                <div className="sidebar-scroll-area">
                    {["Milling Hub", "Designer", "Delivery"].includes(userRole || "") && (
                        <div className="status-wrapper">
                            <label className={`status-card ${isOnline ? "online-bg" : "offline-bg"}`}>
                                <input
                                    type="checkbox"
                                    checked={isOnline}
                                    onChange={async (e) => {

                                        const value = e.target.checked;

                                        const result = await Swal.fire({
                                            title: value ? "Go Online?" : "Go Offline?",
                                            text: value
                                                ? "You will start receiving tasks."
                                                : "You will stop receiving tasks.",
                                            icon: "question",
                                            showCancelButton: true,
                                            confirmButtonText: "Yes",
                                            cancelButtonText: "No",
                                            confirmButtonColor: "#3085d6",
                                            cancelButtonColor: "#d33"
                                        });

                                        // ✅ FIX: explicitly close this confirmation popup
                                        // before triggering the next popup/action, so the
                                        // previous popup's backdrop never lingers on screen.
                                        Swal.close();

                                        if (result.isConfirmed) {
                                            updateOnlineStatus(value);

                                            // ✅ FIX: collapse/close the sidebar after
                                            // confirming online/offline switch — ONLY on
                                            // mobile/tablet, desktop sidebar stays as is.
                                            if (isMobile || isTablet) {
                                                setCollapsed(true);
                                                setOpenMaster(false);
                                            }
                                        }
                                    }}
                                />

                                <span className="toggle-track">
                                    <span className="toggle-knob"></span>
                                </span>
                                {!collapsed && (
                                    <div className="status-text online-offline-text">
                                        <span className="online">Online</span>
                                        <span className="offline">Offline</span>
                                    </div>
                                )}
                            </label>

                            {!collapsed && (
                                <div className="status-labels">
                                    <div className="title-milling-or-design">{getUserInitials(user?.name)}</div>
                                    <div className="subtitle">{subtitle}</div>
                                </div>
                            )}
                        </div>
                    )}
                    <nav className="nav flex-column">
                        {mergedMenu.map((entry: any) => {

                            // ✅ SINGLE MENU ITEM
                            if (entry.type === "item") {
                                return (
                                    <Link
                                        key={entry.key}
                                        to={entry.key}
                                        onClick={handleMenuClick}
                                        className={`nav-link d-flex align-items-center ${collapsed ? "justify-content-center" : "gap-2"
                                            } sidebar-nav-link ${location.pathname.includes(entry.key) ? "active" : ""
                                            }`}
                                    >
                                        <i className={`bi ${entry.icon}`} />
                                        {!collapsed && <span>{entry.label}</span>}
                                    </Link>
                                );
                            }

                            // ✅ GROUP MENU
                            if (entry.type === "group") {
                                const isOpen = openGroups === entry.title;

                                return (
                                    <div className="sidebar-group" key={entry.title}>
                                        <button
                                            className={`nav-link d-flex align-items-center ${collapsed ? "justify-content-center" : "gap-2"
                                                } sidebar-nav-link w-100`}
                                            onClick={() => toggleGroup(entry.title)}
                                        >
                                            <i className={`bi ${entry.icon}`} />
                                            {!collapsed && <span>{entry.title}</span>}
                                            {!collapsed && (
                                                <i
                                                    className={`bi ms-auto ${isOpen ? "bi-chevron-up" : "bi-chevron-down"
                                                        }`}
                                                />
                                            )}
                                        </button>

                                        <div
                                            className={`submenu ${isOpen ? "open" : ""
                                                } ${collapsed ? "collapsed" : ""}`}
                                        >
                                            {entry.items
                                                ?.slice()
                                                .sort((a: any, b: any) => (a.order ?? 999) - (b.order ?? 999))
                                                .map((m: any) => (
                                                    <Link
                                                        key={m.key}
                                                        to={m.key}
                                                        onClick={handleMenuClick}
                                                        className={`nav-link d-flex align-items-center ${collapsed ? "justify-content-center" : "gap-2"
                                                            } sidebar-sublink ${location.pathname === m.key ? "active" : ""
                                                            }`}
                                                    >
                                                        <i className={`bi ${m.icon}`} />
                                                        {!collapsed && <span>{m.label}</span>}
                                                    </Link>
                                                ))}
                                        </div>
                                    </div>
                                );
                            }

                            return null;
                        })}
                    </nav>
                    {/* <nav className="nav flex-column ">
                        {menu.map((m) => {
                            const isDisabled = (m as any).disabled === true;
                            return (
                                <Link
                                    key={m.key}
                                    to={isDisabled ? "#" : m.key}
                                    onClick={(e) => {
                                        if (isDisabled) {
                                            e.preventDefault();
                                            toast.warning("Can't access without clinic approval");
                                            return;
                                        }
                                        handleMenuClick();
                                    }}
                                    className={`nav-link d-flex align-items-center
                                        ${collapsed ? "justify-content-center" : "gap-2"}
                                        sidebar-nav-link
                                        ${location.pathname.includes(m.key) ? "active" : ""}
                                        ${isDisabled ? "menu-disabled" : ""}`}
                                >
                                    {m.iconImage ? (
                                        <img
                                            src={m.iconImage}
                                            alt={m.label}
                                            className="sidebar-icon"
                                        />
                                    ) : (
                                        <i className={`bi ${m.icon}`} />
                                    )}
                                    {!collapsed && <span>{m.label}</span>}
                                </Link>
                            );
                        })}
                    </nav> */}

                </div>

                {/* <div className="sidebar-footer margin-left-footer-20 d-flex justify-content-center p-2">
                    <div className="help-text d-flex justify-content-center align-items-center gap-2">
                        <span className="chaticon">
                            <i className="bi bi-chat-left-dots-fill"></i>
                        </span>
                        {!collapsed && (
                            <div>
                                <p className="help-title mb-0">Need Help?</p>
                                <p className="help-subtitle mb-0">Chat with Us</p>
                            </div>
                        )}
                    </div>
                </div> */}
            </aside>

            {/* Main Area */}
            <div className="flex-grow-1 d-flex flex-column">
                <nav className="topbar centralhub-topbar px-3 d-flex justify-content-between align-items-center">
                    <div className="dropdown custom-top-dropdown" style={{ position: "relative" }}>
                        {userRole === "Dentist" && (
                            <button
                                className="btn dropdown-toggle "
                                type="button"
                                disabled={!isDashboardPage}
                                onClick={() => {
                                    if (isDashboardPage) {
                                        setClinicOpen((prev) => !prev);
                                    }
                                }}
                            >
                                {selectedClinic
                                    ? `${selectedClinic.clinicName} ${selectedClinic.pincode ?? ""}`
                                    : "Select Clinic"}
                            </button>
                        )}

                        <ul
                            className={`dropdown-menu topmenu-dropdown-inheader ${clinicOpen ? "show" : ""}`}
                            style={{ display: clinicOpen ? "block" : "none" }}
                        >
                            {clinics.length > 0 ? (
                                clinics.map((clinic) => (
                                    <li key={clinic.clinicID}>
                                        <button
                                            type="button"
                                            className="dropdown-item"
                                            onClick={() => {
                                                handleClinicSelect(clinic);
                                                setClinicOpen(false);
                                            }}
                                        >
                                            {clinic.clinicName} {clinic.pincode}
                                        </button>
                                    </li>
                                ))
                            ) : (
                                <li>
                                    <span className="dropdown-item text-muted">
                                        No clinics found
                                    </span>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* ✅ Notification Bell with dynamic URL based on user role */}
                    {showNotifications && userRole && (
                        <div className="d-flex align-items-center gap-2">
                            <NotificationBell
                                apiNotificationUrl={getNotificationUrl()}
                                userRole={userRole}
                            />

                            {/* Profile Dropdown */}
                            <div
                                ref={dropdownRef}
                                className="btn w-100 d-flex align-items-center gap-2 ps-dropdown-btn"
                            >
                                <div className="dropdown w-100">
                                    <button
                                        type="button"
                                        className="btn d-flex align-items-center p-0 ps-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setOpen((prev) => !prev);
                                        }}
                                    >
                                        {/* <div className="rounded-circle ps-div d-flex justify-content-center align-items-center">
        {getUserInitials(user?.name)}
      </div> */}

                                        <div className="rounded-circle ps-div d-flex justify-content-center align-items-center overflow-hidden">
                                            <div className="rounded-circle ps-div d-flex justify-content-center align-items-center overflow-hidden">
                                                {userProfileImage && !imgError ? (
                                                    <img
                                                        src={userProfileImage}
                                                        alt="Profile"
                                                        onError={(e) => {
                                                            const target = e.target as HTMLImageElement;
                                                            target.onerror = null; // ✅ prevent infinite loop
                                                            target.src = PredefinedWord.images.profile;
                                                        }}
                                                        style={{
                                                            width: "40px",
                                                            height: "40px",
                                                            objectFit: "cover"
                                                        }}
                                                    />
                                                ) : (
                                                    <span>
                                                        {getUserInitials(user?.name)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <i className="ms-2 bi bi-chevron-down text-secondary"></i>
                                    </button>

                                    <ul
                                        className={`dropdown-menu dropdown-menu-end custom-dropdownps ${open ? "show" : ""
                                            }`}
                                    >
                                        <li>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setOpen(false);
                                                    handleProfileClick();
                                                }}
                                            >
                                                Profile
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setOpen(false);
                                                    handleChangePasswordClick();
                                                }}
                                            >
                                                Change Password
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setOpen(false);
                                                    handleLogout();
                                                }}
                                            >
                                                Logout
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}
                </nav>

                <main className="p-3 p-md-4">{children || <Outlet />}</main>
            </div>
        </div>
    );
};

export default GenericLayout;
